import { Check, ChevronDown, Search, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../lib/cn.js';
import { TRANSITION } from '../motion.js';
import { CONTROL_BASE, CONTROL_BUTTON, CONTROL_OPEN, CONTROL_TONE } from './Field.js';
import { SpringCheck } from './SpringCheck.js';

export interface MultiSelectOption {
  readonly value: string;
  readonly label: string;
  /** Segunda linha: razão social, cargo, o que desambigua homônimos. */
  readonly detail?: string | undefined;
  /**
   * Termos extras que a busca também alcança, sem aparecer na lista.
   *
   * É o que permite achar uma empresa digitando o CNPJ, que ninguém decora mas
   * todo mundo tem à mão no contrato.
   */
  readonly terms?: readonly string[] | undefined;
  /** Ícone à esquerda do rótulo — a situação de uma tarefa, por exemplo. */
  readonly icon?: ReactNode | undefined;
}

/** Normaliza para a busca ignorar acento, caixa e pontuação de CNPJ. */
function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[.\-/]/g, '');
}

function matches(option: MultiSelectOption, term: string): boolean {
  if (term === '') return true;

  const needle = normalize(term);
  const haystack = [option.label, option.detail ?? '', ...(option.terms ?? [])];

  return haystack.some((piece) => normalize(piece).includes(needle));
}

const ALTURA_MAXIMA = 320;

/**
 * Onde o painel é pendurado no DOM.
 *
 * Dentro de um `<dialog>`, o painel precisa ser filho do próprio diálogo: um
 * portal para o `body` cairia **atrás** do modal, porque o diálogo modal vive na
 * top layer e o resto da página não. Fora dele, o `body` serve. É também o que
 * descarta o Combobox do Headless UI: ele monta o painel no `body`, sempre.
 */
function destinoDoPortal(gatilho: HTMLElement | null): HTMLElement {
  return gatilho?.closest('dialog') ?? document.body;
}

/** As opções do painel, na ordem em que aparecem. */
function opcoesDe(painel: HTMLElement | null): HTMLElement[] {
  return Array.from(painel?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
}

/**
 * Painel suspenso compartilhado pelos dois seletores.
 *
 * **Sai do fluxo por portal, com posição fixa.** Como `absolute` dentro do
 * gatilho, ele era recortado por qualquer ancestral com `overflow` — dentro de
 * um modal ficava engolido pelo corpo rolável em vez de se sobrepor.
 *
 * **Delimitado e de cantos retos.** Superfície com filete `line-2` e a sombra
 * de destaque: sobre um formulário claro, o filete tênue de cartão não dizia
 * onde o painel começava nem onde terminava. Cantos retos como o campo que o
 * abre — é a continuação dele. A busca fica numa faixa própria no alto, com
 * divisória, e não rola com a lista. Abre crescendo de 98% a partir do
 * gatilho, em 160ms.
 *
 * **Teclado.** As setas andam pelas opções (e voltam à busca, quando há), e
 * `Enter` escolhe — a opção com foco é a que está realçada. `Esc` fecha e
 * devolve o foco ao gatilho; `Tab` fecha e segue adiante.
 */
function Panel({
  id,
  label,
  multiple,
  searchable,
  term,
  onTerm,
  anchorRef,
  panelRef,
  onClose,
  footer,
  children,
}: {
  id: string;
  label: string;
  multiple: boolean;
  searchable: boolean;
  term: string;
  onTerm: (value: string) => void;
  anchorRef: RefObject<HTMLButtonElement | null>;
  panelRef: RefObject<HTMLDivElement | null>;
  onClose: () => void;
  footer?: ReactNode | undefined;
  children: ReactNode;
}) {
  const searchRef = useRef<HTMLInputElement>(null);
  const [montagem, setMontagem] = useState<{
    destino: HTMLElement;
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    paraCima: boolean;
  } | null>(null);

  /*
   * Mede antes de pintar, e remede a cada rolagem ou redimensionamento — com
   * `capture`, para pegar também a rolagem de um contêiner interno, que não
   * borbulha até a janela.
   */
  useLayoutEffect(() => {
    const medir = (): void => {
      const gatilho = anchorRef.current;
      if (!gatilho) return;

      const caixa = gatilho.getBoundingClientRect();
      const abaixo = window.innerHeight - caixa.bottom - 16;
      const acima = caixa.top - 16;
      const paraCima = abaixo < 200 && acima > abaixo;
      const altura = Math.min(ALTURA_MAXIMA, paraCima ? acima : abaixo);

      setMontagem({
        destino: destinoDoPortal(gatilho),
        top: paraCima ? caixa.top - altura - 4 : caixa.bottom + 4,
        left: caixa.left,
        width: caixa.width,
        maxHeight: altura,
        paraCima,
      });
    };

    medir();
    window.addEventListener('scroll', medir, true);
    window.addEventListener('resize', medir);
    return () => {
      window.removeEventListener('scroll', medir, true);
      window.removeEventListener('resize', medir);
    };
  }, [anchorRef]);

  /*
   * Sem busca, o foco entra direto na lista — na opção já escolhida, ou na
   * primeira — para as setas funcionarem sem um Tab antes. Com busca, o
   * `autoFocus` do campo cuida disso.
   */
  useEffect(() => {
    if (searchable || !montagem) return;
    const opcoes = opcoesDe(panelRef.current);
    const alvo =
      opcoes.find((opcao) => opcao.getAttribute('aria-selected') === 'true') ?? opcoes[0];
    alvo?.focus({ preventScroll: false });
  }, [searchable, montagem, panelRef]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const opcoes = opcoesDe(panelRef.current);
    const atual = document.activeElement as HTMLElement | null;
    const indice = atual ? opcoes.indexOf(atual) : -1;

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        opcoes[Math.min(indice + 1, opcoes.length - 1)]?.focus();
        return;
      }
      case 'ArrowUp': {
        event.preventDefault();
        if (indice <= 0 && searchable) searchRef.current?.focus();
        else opcoes[Math.max(indice - 1, 0)]?.focus();
        return;
      }
      case 'Home': {
        if (atual === searchRef.current) return;
        event.preventDefault();
        opcoes[0]?.focus();
        return;
      }
      case 'End': {
        if (atual === searchRef.current) return;
        event.preventDefault();
        opcoes[opcoes.length - 1]?.focus();
        return;
      }
      case 'Escape': {
        event.preventDefault();
        onClose();
        anchorRef.current?.focus();
        return;
      }
      case 'Tab': {
        // Fecha e devolve o foco ao gatilho: o Tab segue dali, na ordem da página.
        onClose();
        anchorRef.current?.focus();
        return;
      }
      default:
        return;
    }
  };

  // Sem medida ainda: o portal só existe depois do primeiro `useLayoutEffect`,
  // o que também evita ler a ref durante a renderização.
  if (!montagem) return null;

  const deslocamento = montagem.paraCima ? 4 : -4;

  return createPortal(
    <motion.div
      ref={panelRef}
      id={id}
      role="listbox"
      aria-label={label}
      aria-multiselectable={multiple || undefined}
      initial={{ opacity: 0, scale: 0.98, y: deslocamento }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.98, y: deslocamento }}
      transition={TRANSITION.fast}
      style={{
        position: 'fixed',
        top: montagem.top,
        left: montagem.left,
        minWidth: montagem.width,
        maxHeight: montagem.maxHeight,
        transformOrigin: montagem.paraCima ? 'bottom left' : 'top left',
      }}
      className="z-50 flex w-72 flex-col overflow-hidden border border-line-2 bg-surface text-ink shadow-pop"
      onKeyDown={onKeyDown}
    >
      {searchable ? (
        <div className="flex h-10 shrink-0 items-center gap-2 border-b border-line px-3">
          <Search size={15} strokeWidth={1.5} className="shrink-0 text-ink-3" aria-hidden />
          <input
            ref={searchRef}
            autoFocus
            value={term}
            onChange={(event) => onTerm(event.target.value)}
            placeholder="Buscar…"
            aria-label={`Buscar em ${label.toLowerCase()}`}
            className="h-full min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-3"
          />
          {term ? (
            <button
              type="button"
              onClick={() => {
                onTerm('');
                searchRef.current?.focus();
              }}
              aria-label="Limpar busca"
              className="grid size-6 shrink-0 place-items-center text-ink-3 transition-colors hover:bg-sunken hover:text-ink"
            >
              <X size={13} strokeWidth={2} aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto py-1">{children}</div>

      {footer}
    </motion.div>,
    montagem.destino,
  );
}

/**
 * Fecha ao clicar fora.
 *
 * "Fora" é fora do gatilho **e** fora do painel: com o painel num portal, ele
 * deixou de ser descendente do contêiner, e clicar numa opção fechava o menu
 * antes de a escolha ser registrada. `Esc` e `Tab` são tratados no painel,
 * que é onde o foco está.
 */
function useDismiss(
  open: boolean,
  close: () => void,
): {
  containerRef: RefObject<HTMLDivElement | null>;
  panelRef: RefObject<HTMLDivElement | null>;
} {
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      const alvo = event.target as Node;
      if (containerRef.current?.contains(alvo)) return;
      if (panelRef.current?.contains(alvo)) return;
      close();
    };
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  return { containerRef, panelRef };
}

/** Abre com `Enter`, `Espaço` ou seta para baixo; o clique já abre sozinho. */
function abrirPeloTeclado(event: KeyboardEvent<HTMLButtonElement>, abrir: () => void): void {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault();
    abrir();
  }
}

/**
 * Uma opção da lista.
 *
 * Na seleção múltipla, a caixa com mola à esquerda; na única, o visto em
 * `primary` à direita da escolhida. O realce do teclado é o mesmo do ponteiro
 * — o fundo afundado —, sem anel: numa lista, anel por opção vira cerca.
 */
function Opcao({
  option,
  active,
  multiple,
  onSelect,
}: {
  option: MultiSelectOption;
  active: boolean;
  multiple: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onClick={onSelect}
      className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors outline-none hover:bg-sunken focus-visible:bg-sunken"
    >
      {multiple ? <SpringCheck checked={active} size={16} /> : null}
      {option.icon ? <span className="grid shrink-0 place-items-center">{option.icon}</span> : null}

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm">{option.label}</span>
        {option.detail ? (
          <span className="block truncate text-[11px] text-ink-3">{option.detail}</span>
        ) : null}
      </span>

      {!multiple && active ? (
        <Check size={15} strokeWidth={2} className="shrink-0 text-primary" aria-hidden />
      ) : null}
    </button>
  );
}

function NadaEncontrado() {
  return <p className="px-3 py-3 text-sm text-ink-3">Nada encontrado.</p>;
}

function Chevron({ open }: { open: boolean }) {
  return (
    <ChevronDown
      size={15}
      strokeWidth={1.5}
      className={cn('shrink-0 text-ink-3 transition-transform duration-200', open && 'rotate-180')}
      aria-hidden
    />
  );
}

/**
 * O gatilho: a casca do campo, sem ícone à esquerda. Com
 * seleção (no filtro) ou aberto, o sublinhado é laranja.
 */
function gatilho(destacado: boolean): string {
  return cn(
    CONTROL_BASE,
    'flex h-10 items-center gap-2 text-left text-sm',
    CONTROL_BUTTON,
    destacado ? CONTROL_OPEN : CONTROL_TONE.rest,
  );
}

/**
 * Seleção múltipla em menu suspenso.
 *
 * Nada de `<select multiple>`: ele exige Ctrl+clique para escolher mais de um
 * item, o que quase ninguém descobre.
 */
export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  searchable = false,
  variant = 'filter',
}: {
  label: string;
  options: readonly MultiSelectOption[];
  selected: readonly string[];
  onChange: (values: string[]) => void;
  /** Liga o campo de busca — vale a partir de umas dez opções. */
  searchable?: boolean;
  /**
   * `filter` é o chip da barra de filtros: com seleção, o sublinhado fica
   * laranja e o contador vai num selo laranja, com um "limpar" ao lado e
   * no rodapé do painel. `field` é campo de formulário — largura inteira, as
   * escolhas escritas na linha e **sem** o limpar, que ali apagaria escolhas de
   * verdade em vez de desfazer um recorte.
   */
  variant?: 'filter' | 'field';
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const panelId = useId();
  const gatilhoRef = useRef<HTMLButtonElement>(null);

  /*
   * Fechar limpa a busca. Ficava num efeito olhando `open`, o que dava o mesmo
   * resultado um quadro depois — tempo suficiente para o painel reabrir com o
   * termo antigo ainda na tela.
   */
  const fechar = useCallback(() => {
    setOpen(false);
    setTerm('');
  }, []);

  const { containerRef, panelRef } = useDismiss(open, fechar);

  function toggle(value: string): void {
    onChange(
      selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value],
    );
  }

  const visible = options.filter((option) => matches(option, term));
  const count = selected.length;
  const isField = variant === 'field';

  /* Como campo, o valor é a lista do que foi escolhido, cortada no fim da linha. */
  const resumo = options
    .filter((option) => selected.includes(option.value))
    .map((option) => option.label)
    .join(', ');

  return (
    <div ref={containerRef} className={cn('group relative', isField && 'w-full')}>
      <button
        ref={gatilhoRef}
        type="button"
        onClick={() => (open ? fechar() : setOpen(true))}
        onKeyDown={(event) => abrirPeloTeclado(event, () => setOpen(true))}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? panelId : undefined}
        aria-label={isField ? label : undefined}
        className={gatilho(open || (!isField && count > 0))}
      >
        {isField ? (
          <>
            <span className={cn('min-w-0 flex-1 truncate', count === 0 && 'text-ink-3')}>
              {count > 0 ? resumo : label}
            </span>
            {count > 0 ? (
              <span className="tabular bg-surface px-1.5 text-[11px] font-medium text-ink-2">
                {count}
              </span>
            ) : null}
          </>
        ) : (
          <>
            <span className={cn('truncate', count > 0 && 'font-medium')}>{label}</span>
            {count > 0 ? (
              <span className="tabular bg-primary px-1.5 text-[11px] font-medium text-on-primary">
                {count}
              </span>
            ) : null}
          </>
        )}

        <Chevron open={open} />
      </button>

      {count > 0 && !isField ? (
        <button
          type="button"
          onClick={() => onChange([])}
          aria-label={`Limpar filtro de ${label.toLowerCase()}`}
          className="absolute top-1/2 -right-1 grid size-5 -translate-y-1/2 translate-x-full place-items-center text-ink-3 transition-colors hover:bg-sunken hover:text-ink"
        >
          <X size={13} strokeWidth={2} aria-hidden />
        </button>
      ) : null}

      <AnimatePresence>
        {open ? (
          <Panel
            key="painel"
            id={panelId}
            label={label}
            multiple
            searchable={searchable}
            term={term}
            onTerm={setTerm}
            anchorRef={gatilhoRef}
            panelRef={panelRef}
            onClose={fechar}
            footer={
              !isField && count > 0 ? (
                <div className="flex shrink-0 items-center justify-between gap-3 border-t border-line px-3 py-1.5 text-[12px] text-ink-3">
                  <span className="tabular">
                    {count} {count === 1 ? 'item' : 'itens'}
                  </span>
                  <button
                    type="button"
                    onClick={() => onChange([])}
                    className="font-medium text-ink transition-colors hover:text-primary-ink"
                  >
                    Limpar
                  </button>
                </div>
              ) : undefined
            }
          >
            {visible.length === 0 ? (
              <NadaEncontrado />
            ) : (
              visible.map((option) => (
                <Opcao
                  key={option.value}
                  option={option}
                  multiple
                  active={selected.includes(option.value)}
                  onSelect={() => toggle(option.value)}
                />
              ))
            )}
          </Panel>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/**
 * Escolha única em menu suspenso, com busca opcional.
 *
 * Mesma aparência da seleção múltipla, e o mesmo motivo para não ser um
 * `<select>` nativo: com busca e duas linhas por opção, o controle do navegador
 * não dá conta.
 */
export function SingleSelect({
  label,
  options,
  value,
  onChange,
  searchable = false,
  placeholder,
  className,
  renderTrigger,
  triggerClassName,
}: {
  /** Nome acessível do gatilho; como filtro, também o texto de repouso quando não há `placeholder`. */
  label: string;
  options: readonly MultiSelectOption[];
  value: string | null;
  onChange: (value: string) => void;
  searchable?: boolean;
  placeholder?: string;
  className?: string;
  /**
   * O miolo do gatilho, no lugar do rótulo e da seta — um ícone só, por
   * exemplo. O painel, o teclado e a acessibilidade continuam os mesmos.
   */
  renderTrigger?:
    ((atual: MultiSelectOption | undefined, aberto: boolean) => ReactNode) | undefined;
  /** A casca do gatilho, no lugar da de campo; vale com `renderTrigger`. */
  triggerClassName?: string | undefined;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const panelId = useId();
  const gatilhoRef = useRef<HTMLButtonElement>(null);

  const fechar = useCallback(() => {
    setOpen(false);
    setTerm('');
  }, []);

  const { containerRef, panelRef } = useDismiss(open, fechar);

  const current = options.find((option) => option.value === value);
  const visible = options.filter((option) => matches(option, term));

  return (
    <div ref={containerRef} className={cn('group relative', className)}>
      <button
        ref={gatilhoRef}
        type="button"
        onClick={() => (open ? fechar() : setOpen(true))}
        onKeyDown={(event) => abrirPeloTeclado(event, () => setOpen(true))}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? panelId : undefined}
        aria-label={label}
        className={triggerClassName ?? gatilho(open)}
      >
        {renderTrigger ? (
          renderTrigger(current, open)
        ) : (
          <>
            <span className={cn('min-w-0 flex-1 truncate', !current && 'text-ink-3')}>
              {current?.label ?? placeholder ?? label}
            </span>
            <Chevron open={open} />
          </>
        )}
      </button>

      <AnimatePresence>
        {open ? (
          <Panel
            key="painel"
            id={panelId}
            label={label}
            multiple={false}
            searchable={searchable}
            term={term}
            onTerm={setTerm}
            anchorRef={gatilhoRef}
            panelRef={panelRef}
            onClose={fechar}
          >
            {visible.length === 0 ? (
              <NadaEncontrado />
            ) : (
              visible.map((option) => (
                <Opcao
                  key={option.value}
                  option={option}
                  multiple={false}
                  active={option.value === value}
                  onSelect={() => {
                    onChange(option.value);
                    fechar();
                    gatilhoRef.current?.focus();
                  }}
                />
              ))
            )}
          </Panel>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
